import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { cn } from '../lib/cn';
import { LogOut, Users, Shield, FileSearch, Building2 } from 'lucide-react';

/**
 * Main application shell — horizontal top bar + sidebar + content area.
 * No persistent impersonation banner yet (Phase 2 only wires that in).
 */
export function Layout() {
  const auth = useAuth();
  if (auth.kind !== 'signed-in') return null;

  const { claims } = auth;
  const isSuperadmin = auth.hasRole('superadmin');

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <header className="border-b border-border bg-surface h-12 flex items-center justify-between px-4">
        <div className="flex items-center gap-6">
          <div className="font-semibold text-fg">StrattoVoice Admin</div>
          <div className="text-xs text-muted">
            {import.meta.env.MODE === 'production' ? 'production' : 'dev'}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted">
          <span>
            {claims.email} · <span className="uppercase tracking-wide">{claims.role}</span>
          </span>
          <button
            onClick={() => auth.signOut()}
            className="flex items-center gap-1 text-muted hover:text-fg"
            title="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span>Sign out</span>
          </button>
        </div>
      </header>

      {/* Body = sidebar + main */}
      <div className="flex-1 flex">
        <aside className="w-48 border-r border-border bg-surface py-3">
          <nav className="flex flex-col gap-0.5 px-2 text-sm">
            <NavItem to="/tenants" icon={<Building2 className="h-4 w-4" />}>
              Tenants
            </NavItem>
            <NavItem to="/audit" icon={<FileSearch className="h-4 w-4" />}>
              Audit Log
            </NavItem>
            {isSuperadmin && (
              <NavItem to="/admins" icon={<Shield className="h-4 w-4" />}>
                Admin Accounts
              </NavItem>
            )}
            {/* Phase 2+ items will appear here */}
            <div className="mt-auto pt-4 px-2 text-xs text-muted">
              <span className="flex items-center gap-1.5">
                <Users className="h-3 w-3" /> Phase 1
              </span>
            </div>
          </nav>
        </aside>

        <main className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 px-2 py-1.5 rounded hover:bg-border/40',
          isActive && 'bg-border/60 text-fg font-medium',
        )
      }
    >
      {icon}
      <span>{children}</span>
    </NavLink>
  );
}
